import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";

import { AuthProvider } from "./context/AuthContext";
import { usePageView } from "./hooks/usePageView";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ServicePage from "./pages/ServicePage";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";

// Global Lenis smooth momentum scroll (excluded from admin routes)
function useLenisSmoothScroll() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) return;
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    const rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [isAdmin]);
}

// Redirects a clean SPA URL to a standalone static page from /public.
// Skipped during react-snap prerendering so snapshots stay intact.
function StaticRedirect({ to }) {
  useEffect(() => {
    if (!navigator.userAgent.includes("ReactSnap")) {
      window.location.replace(to);
    }
  }, [to]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  usePageView(location.pathname);
  useLenisSmoothScroll();

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />

      {/* Blog */}
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      {/* Standalone static pages (served from public/, redirect to keep clean URLs) */}
      <Route path="/momentum-method" element={<StaticRedirect to="/momentum-method.html" />} />
      <Route path="/privacy-policy" element={<StaticRedirect to="/privacy-policy.html" />} />
      <Route path="/terms" element={<StaticRedirect to="/terms-of-service.html" />} />
      <Route path="/terms-of-service" element={<StaticRedirect to="/terms-of-service.html" />} />

      {/* Service pages — Brisbane keyword landing pages (wildcard, filtered inside ServicePage) */}
      <Route path="/business-cards-brisbane" element={<ServicePage slugOverride="business-cards-brisbane" />} />
      <Route path="/signage-brisbane" element={<ServicePage slugOverride="signage-brisbane" />} />
      <Route path="/banners-brisbane" element={<ServicePage slugOverride="banners-brisbane" />} />
      <Route path="/publications-brisbane" element={<ServicePage slugOverride="publications-brisbane" />} />
      <Route path="/promotional-items-brisbane" element={<ServicePage slugOverride="promotional-items-brisbane" />} />
      <Route path="/marketing-campaigns-brisbane" element={<ServicePage slugOverride="marketing-campaigns-brisbane" />} />

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
